import type { PreferredDesigner } from "@nlc/shared-types";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";

import { useEffect } from "react";
import { Controller } from "react-hook-form";
import { useListData } from "react-stately";

import { Avatar } from "@/components/avatar";
import { Label } from "@/components/field";
import {
  MultiSelect,
  MultiSelectField,
  MultiSelectItem,
} from "@/components/multi-select";
import { Select, SelectButton } from "@/components/select";
import { Tag } from "@/components/tag-group";
import { useGetPreferredDesigners } from "@/features/request/shared/UseGetPreferredDesigners.tsx";
import { useCapturePosthogEvent } from "@/hooks/posthog/useCapturePosthogEvent.tsx";
import { useGetCurrentProjectId } from "@/hooks/useGetCurrentProjectId.tsx";

export function PreferredDesignersSelect<T extends FieldValues>({
  className,
  fieldName = "preferredDesignerIds" as Path<T>,
  methods,
}: {
  className?: string;
  fieldName?: Path<T>;
  methods: UseFormReturn<T>;
}) {
  const capture = useCapturePosthogEvent();
  const projectId = useGetCurrentProjectId();
  const { data } = useGetPreferredDesigners(projectId);

  const defaultItems: number[] = methods.watch(fieldName) ?? [];

  const selectedList = useListData<PreferredDesigner>({
    getKey: (item) => item.id,
    initialItems: [],
  });

  useEffect(() => {
    if (data) {
      selectedList.remove(...selectedList.items.map((item) => item.id));
      selectedList.append(
        ...data.filter((item) => defaultItems.includes(item.id)),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultItems, data]);

  if (data) {
    return (
      <Controller
        control={methods.control}
        name={fieldName}
        render={({ field: { name, onBlur, onChange, value } }) => (
          <MultiSelectField className={className}>
            <Label>Preferred designers</Label>
            <MultiSelect<PreferredDesigner>
              items={data}
              name={name}
              onBlur={onBlur}
              onItemAdd={(key) => {
                const newValue = [...(value || []), key];

                capture("designer_preference_set");
                onChange(newValue);
              }}
              onItemRemove={(key) => {
                const newValue = (value || []).filter(
                  (id: number) => id !== key,
                );

                onChange(newValue);
              }}
              placeholder="Select"
              renderEmptyState={(inputValue) => (
                <span className="p-2 text-text-primary">
                  {inputValue ? (
                    <>No results found for: {inputValue}</>
                  ) : (
                    `No designers available`
                  )}
                </span>
              )}
              selectedList={selectedList}
              tag={(item) => (
                <Tag textValue={String(item.id)}>
                  <Avatar
                    alt={item.textValue}
                    className="rounded-full size-3"
                    src={item.profilePicture}
                  />
                  {item.textValue}
                </Tag>
              )}
            >
              {(item) => (
                <MultiSelectItem textValue={String(item.id)}>
                  {item.textValue}
                </MultiSelectItem>
              )}
            </MultiSelect>
          </MultiSelectField>
        )}
      />
    );
  }

  return (
    <Select className={className} placeholder="Loading...">
      <Label>Preferred designers</Label>
      <SelectButton />
    </Select>
  );
}
